import VueRouter from 'vue-router';
import {
    getLanguageForCountry,
    ipCountryQuery,
    languageQuery,
} from '@gig/magic-common';
import { config } from '../../config';
import { LANGUAGE_COMMAND_CHANGE } from '@gig/magic-messages';
import { Country } from '@gig/magic-sdk/dist/adapter/users/getCountries';
import { Language } from '@gig/magic-sdk/dist/adapter/users/language';
import { RouteName } from '../../../routes/routes.constants';

const ENGLISH_SPEAKING_COUNTRIES_MAP = {
    [Country.UNITED_KINGDOM]: Language.BRITISH,
    [Country.IRELAND]: Language.IRISH,
    [Country.CANADA]: Language.CANADIAN,
    [Country.NEW_ZEALAND]: Language.NEW_ZEALAND,
    [Country.SOUTH_AFRICA]: Language.SOUTH_AFRICAN,
    [Country.INDIA]: Language.HINDI,
};


const RESTRICTED_LANGUAGES = Object.values(ENGLISH_SPEAKING_COUNTRIES_MAP);
type CountryLanguageKey = keyof typeof ENGLISH_SPEAKING_COUNTRIES_MAP;

const updateLang = (path: string, lang: string) => {
    const langRegExp = /^\/[a-z]{2}(\/|$)/;
    return langRegExp.test(path)
        ? path.replace(langRegExp, `/${lang}$1`)
        : `/${lang}${path}`;
};

const hasCountrySpecificLanguage = (country: Country): boolean => {
    return country in ENGLISH_SPEAKING_COUNTRIES_MAP;
};

const getCountrySpecificLanguage = (country: Country): Language => {
    return ENGLISH_SPEAKING_COUNTRIES_MAP[country as CountryLanguageKey] ||
        config.languages.default;
};

const canAccessLanguage = (userCountry: Country, requestedLanguage: string): boolean => {
    if (!hasCountrySpecificLanguage(userCountry)) {
        return true;
    }

    if (!RESTRICTED_LANGUAGES.includes(requestedLanguage as Language)) {
        return true;
    }

    const countrySpecificLanguage = getCountrySpecificLanguage(userCountry);
    return requestedLanguage === countrySpecificLanguage;
};

export const initLanguageGuard = (router: VueRouter) => {
    router.beforeEach(async (to, _, next) => {
        const countryCode = await ipCountryQuery();
        const langForCountry = getLangForCountry(countryCode);
        const path = to.redirectedFrom || to.fullPath;
        const currentLanguage = await languageQuery();

        if (!to.params.language) {
            if (hasCountrySpecificLanguage(countryCode)) {
                return next({
                    path: updateLang(path, langForCountry),
                    replace: true,
                });
            }
            return next({
                path: `/${
                    window.location.pathname.includes('/nw')
                        ? Language.NW_NORWEGIAN
                        : currentLanguage
                }${path}`,
                replace: true,
            });
        }

        const requestedLanguage = to.params.language;

        if (!canAccessLanguage(countryCode, requestedLanguage)) {
            return next({
                path: updateLang(path, langForCountry),
                replace: true,
            });
        }

        if (to.params.language === Language.ENGLISH && hasCountrySpecificLanguage(countryCode)) {
            return next({
                path: updateLang(path, getCountrySpecificLanguage(countryCode)),
                replace: true,
            });
        }

        // Block Swedish language for non-Swedish users
        if (
            countryCode !== Country.SWEDEN &&
            to.params.language === Language.SWEDISH
        ) {
            return next({
                name: RouteName.PAGE404,
                params: {
                    language: langForCountry,
                },
                replace: true,
            });
        }

        const isValidLanguage = config.languages.available.includes(
            to.params.language,
        );

        const countryIsLocked =
            countryCode &&
            config.countries.lockedCountries.includes(countryCode);

        if (
            !isValidLanguage ||
            (countryIsLocked && to.params.language !== langForCountry)
        ) {
            next({
                path: updateLang(path, langForCountry),
                replace: true,
            });
            return;
        }

        return next();
    });

    router.afterEach(async (to) => {
        const countryCode = await ipCountryQuery();
        const newLanguage = to.params.language;
        const oldLanguage = await languageQuery();

        const isNWLanguage = newLanguage === Language.NW_NORWEGIAN && oldLanguage === Language.NORWEGIAN;

        if (newLanguage !== oldLanguage && !isNWLanguage) {
            // Handle country-specific language variants for English
            if (countryCode && hasCountrySpecificLanguage(countryCode) &&
                newLanguage === Language.ENGLISH) {
                window.commandBus.publish(
                    LANGUAGE_COMMAND_CHANGE,
                    getCountrySpecificLanguage(countryCode)
                );
                return;
            }

            window.commandBus.publish(LANGUAGE_COMMAND_CHANGE, newLanguage);
        }
    });
};

function getLangForCountry(country: Country): Language | string {
    if (!country) {
        return config.languages.default;
    }

    if (hasCountrySpecificLanguage(country)) {
        return getCountrySpecificLanguage(country);
    }

    const langForCountry = getLanguageForCountry(country);

    return config.languages.available.includes(langForCountry)
        ? langForCountry
        : config.languages.default;
}
